/**
 * `button` — `ButtonValue` (`specs/M2-tier2-column-types.md` §5.16, `[09§A.7 button]`).
 *
 * *"button state is managed by automations, not user data."* The only stored value is the click
 * counter — `{"clicks":1,"changed_at":…}`, null on a row nobody clicked (99 of 100 sampled
 * cells) — while the **label and colour come from the column settings**, which is why `text` is
 * non-empty on every cell of the column, clicked or not `[cvs§2 button]`.
 *
 * Pressing it fires the automation trigger `button_clicked`
 * (`packages/shared/src/automations/triggers/button-clicked`), registered by this round; the
 * counter is bumped by the service on the same write.
 */
import { z } from 'zod';
import { defaultSummarySql } from '../sql.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const buttonValueSchema = z.looseObject({
    clicks: z.number().int().min(0),
    changed_at: z.string().nullable().optional(),
});
import { buttonFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const buttonSettingsSchema = z.looseObject({
    /** *"defaults to `Click me`"* `[09§A.7 button]`. */
    buttonText: z.string().optional(),
    /** Observed `#579bfc` / `#037f4c` on this account `[cvs§2 button]`. */
    color: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
export const BUTTON_DEFAULT_LABEL = 'Click me';
export const BUTTON_DEFAULT_COLOR = '#579bfc';
export function buttonLabel(settings) {
    const label = settings.buttonText;
    return label === undefined || label === '' ? BUTTON_DEFAULT_LABEL : label;
}
function coerce(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    const clicks = typeof raw === 'object' ? raw.clicks : raw;
    if (clicks === null || clicks === undefined || clicks === '')
        return null;
    const count = Number(clicks);
    if (!Number.isFinite(count) || count <= 0)
        return null;
    const value = { clicks: Math.trunc(count) };
    if (keepVolatile && typeof raw === 'object') {
        const source = raw;
        if ('changed_at' in source)
            value.changed_at = source.changed_at;
    }
    return value;
}
export const buttonColumn = defineColumnType({
    type: 'button',
    title: 'Button',
    description: 'A button that fires an automation when clicked.',
    apiFragmentName: 'ButtonValue',
    legacyTypeName: 'button',
    idPrefix: 'button',
    valueSchema: buttonValueSchema,
    settingsSchema: buttonSettingsSchema,
    defaultSettings: { buttonText: BUTTON_DEFAULT_LABEL },
    settingsStrSchema: buttonSettingsSchema,
    /**
     * Not writable through `change_column_value`: the click counter is bumped by the click
     * endpoint, exactly as monday's is by its own UI `[09§A.7 button]`.
     */
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    filterConditions: buttonFilterConditions,
    filterOperators: operatorsOf(buttonFilterConditions),
    aggregations: ['count', 'none'],
    /**
     * Never empty: monday's `text` is the settings label on **every** cell of the column,
     * clicked or not (100/100 sampled) `[cvs§2 button]`. `emptyText` is a constant, so it
     * carries the default label; `toText` uses the column's own.
     */
    emptyText: BUTTON_DEFAULT_LABEL,
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('button', 'the button column is written by clicking it, not by a cell write');
    },
    /** The label, always — clicked or not (100/100 cells carried it) `[cvs§2 button]`. */
    toText(_value, settings) {
        return buttonLabel(settings);
    },
    toSortKeys(value, settings) {
        // An unclicked cell has no `cell_values` row at all, so its shadows are null (B5 §5.1);
        // a clicked one sorts by the label, which is the only text this column ever shows.
        return value === null
            ? { text: null, number: null, date: null }
            : { text: buttonLabel(settings), number: value.clicks, date: null };
    },
    isEqual(a, b) {
        return (a?.clicks ?? 0) === (b?.clicks ?? 0);
    },
    toActivityValue(value) {
        return value === null ? null : { clicks: value.clicks };
    },
    fromMondayValue(raw) {
        return coerce(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { clicks: value.clicks };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return buttonSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(_value, settings) {
        // `label` and `color` come from the settings, never from the cell `[cvs§2 button]`.
        return { label: buttonLabel(settings), color: settings.color ?? BUTTON_DEFAULT_COLOR };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    filterSql() {
        return null;
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map